'use client';

import React from 'react';
import { cn } from '@/lib/cn';

/** Keep in sync with `--animate-slide-up` / `--animate-slide-out-up` (0.3s). */
const TRANSITION_MS = 300;

export interface RotatingPlaceholderProps {
  text: string;
  /** Mirror the native placeholder: gone as soon as the field has content. */
  hidden: boolean;
  /** Padding / line-height / text-size classes matching the field it overlays. */
  className?: string;
}

/**
 * Animated stand-in for a field's native `placeholder`, which can't be
 * transitioned. On each text change the outgoing line slides up and out while
 * the incoming one slides in from below; the field itself renders no
 * placeholder and relies on its `aria-label` for its accessible name.
 */
export function RotatingPlaceholder({ text, hidden, className }: RotatingPlaceholderProps) {
  // `seq` re-keys both lines so a change mid-animation restarts it cleanly.
  const [phase, setPhase] = React.useState({
    current: text,
    outgoing: null as string | null,
    seq: 0,
  });

  React.useEffect(() => {
    setPhase(p =>
      p.current === text ? p : { current: text, outgoing: p.current, seq: p.seq + 1 },
    );
  }, [text]);

  React.useEffect(() => {
    if (phase.outgoing === null) return;
    const id = window.setTimeout(() => setPhase(p => ({ ...p, outgoing: null })), TRANSITION_MS);
    return () => window.clearTimeout(id);
  }, [phase.seq, phase.outgoing]);

  if (hidden) return null;

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-x-0 top-0 text-tertiary', className)}
    >
      {/* Clip at the line box so the two lines disappear exactly at its edges. */}
      <div className="relative overflow-hidden">
        {phase.outgoing !== null ? (
          <span
            key={`out-${phase.seq}`}
            className="absolute inset-x-0 top-0 block truncate animate-slide-out-up"
          >
            {phase.outgoing}
          </span>
        ) : null}
        <span
          key={`in-${phase.seq}`}
          className={cn('block truncate', phase.outgoing !== null && 'animate-slide-up')}
        >
          {phase.current}
        </span>
      </div>
    </div>
  );
}

export default RotatingPlaceholder;
